import { Link } from "react-router-dom";

const STATUS_BADGE = {
  Draft: "bg-gray-100 text-gray-700",
  "Offered to Vendor": "bg-purple-100 text-purple-700",
  "Offer Accepted": "bg-green-100 text-green-700",
  "Offer Rejected": "bg-red-100 text-red-700",
  Started: "bg-blue-100 text-blue-700",
  Completed: "bg-teal-100 text-teal-700",
  Hold: "bg-yellow-100 text-yellow-700",
  "Completion Accepted": "bg-green-100 text-green-700",
  "Completion Rejected": "bg-red-100 text-red-700",
  Cancelled: "bg-gray-100 text-gray-700",
};

const StatusBadge = ({ status }) => {
  if (!status) return <span className="text-gray-500">—</span>;
  const cls = STATUS_BADGE[status] || "bg-gray-100 text-gray-700";
  return (
    <span className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded ${cls}`}>
      {status}
    </span>
  );
};

const Field = ({ label, value }) => {
  const isEmpty = value === null || value === undefined || value === "";
  return (
    <div className="flex items-start text-sm">
      <span className="w-44 shrink-0 text-xs font-medium text-gray-500 pt-px">
        {label}:
      </span>
      <span className="text-gray-900">{isEmpty ? "—" : value}</span>
    </div>
  );
};

const btnPrimary =
  "px-3 py-1.5 font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded";
const btnSuccess =
  "px-3 py-1.5 font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded";
const btnWarning =
  "px-3 py-1.5 font-medium text-white bg-amber-500 hover:bg-amber-600 rounded";
const btnSecondary =
  "px-3 py-1.5 font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded";
const btnDanger =
  "px-3 py-1.5 font-medium text-white bg-red-600 hover:bg-red-700 rounded";

const AssignmentStatusCard = ({
  job,
  onAccept,
  onReject,
  onCancel,
  onDraft,
  onOffer,
  onStart,
}) => {
  const formatDateTime = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getDeadlineStatus = () => {
    if (!job?.deadline_at) return null;
    const now = new Date();
    const deadline = new Date(job.deadline_at);
    const diffMs = deadline - now;
    if (diffMs < 0) {
      const absMs = Math.abs(diffMs);
      const days = Math.floor(absMs / (1000 * 60 * 60 * 24));
      const hours = Math.floor((absMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      return {
        text: `Overdue by ${days} day${days !== 1 ? "s" : ""}${
          hours ? `, ${hours} hour${hours !== 1 ? "s" : ""}` : ""
        }`,
        isOverdue: true,
      };
    }
    return { text: formatDateTime(job.deadline_at), isOverdue: false };
  };

  const deadlineStatus = getDeadlineStatus();

  const getVendorName = () => {
    if (!job?.vendor) return null;
    return (
      job.vendor.company_name ||
      `${job.vendor.primary_users?.first_name || ""} ${
        job.vendor.primary_users?.last_name || ""
      }`.trim()
    );
  };

  const getVendorContactName = () => {
    if (!job?.vendorContactPerson) return null;
    return `${job.vendorContactPerson.first_name} ${job.vendorContactPerson.last_name}`;
  };

  const showActionRequired =
    job?.status === "Completed" || job?.status === "Offer Accepted";

  const getActionMessage = () => {
    if (job?.status === "Completed") {
      return "Job is completed and awaiting manager review.";
    }
    if (job?.status === "Offer Accepted") {
      return "Job offer has been accepted by vendor. Ready to start.";
    }
    return "";
  };

  const isOfferedToVendor = job?.status === "Offered to Vendor";

  return (
    <section>
      <div className="flex justify-between items-center mb-3 flex-wrap gap-3">
        <h2 className="text-xl font-bold text-gray-900">Job Assignment & Status</h2>
        <div className="flex items-center gap-2 text-sm flex-wrap">
          {(job?.status === "Draft" || isOfferedToVendor) && (
            <button onClick={!isOfferedToVendor ? onOffer : undefined} disabled={isOfferedToVendor} className={btnPrimary}>
              Offer to Vendor
            </button>
          )}
          {job?.status === "Offer Accepted" && (
            <button onClick={onStart} className={btnPrimary}>Start Job</button>
          )}
          {job?.status === "Completed" && (
            <>
              <button onClick={onAccept} className={btnSuccess}>Accept Completion</button>
              <button onClick={onReject} className={btnWarning}>Reject Completion</button>
              <button onClick={onDraft} className={btnSecondary}>Send to Draft</button>
            </>
          )}
          {(job?.status === "Started" || job?.status === "Hold") && (
            <button onClick={onCancel} className={btnDanger}>Cancel</button>
          )}
        </div>
      </div>

      <div className="bg-white border border-gray-100 shadow-sm rounded-lg p-5 space-y-4">
        {showActionRequired && (
          <div className="flex gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm">
            <span className="font-semibold text-emerald-700">Action required</span>
            <span className="text-emerald-700">{getActionMessage()}</span>
          </div>
        )}

        <div className="space-y-3">
          <Field
            label="Vendor"
            value={
              job?.vendor ? (
                <Link
                  to={`/vendors/${job.vendor.id}`}
                  className="text-blue-600 hover:text-blue-800 underline font-medium"
                >
                  {getVendorName()}
                </Link>
              ) : (
                ""
              )
            }
          />
          <Field label="Vendor contact person" value={getVendorContactName()} />
          <Field label="Status" value={<StatusBadge status={job?.status} />} />
          <Field
            label="Deadline"
            value={
              deadlineStatus?.isOverdue ? (
                <span className="text-red-600 font-medium">{deadlineStatus.text}</span>
              ) : (
                deadlineStatus?.text || ""
              )
            }
          />
          {job?.started_at && (
            <Field label="Started At" value={formatDateTime(job.started_at)} />
          )}
          {job?.completed_at && (
            <Field label="Completed At" value={formatDateTime(job.completed_at)} />
          )}
        </div>
      </div>
    </section>
  );
};

export default AssignmentStatusCard;
